import { useMemo } from "react";
import { normalizeCampaignBrief, normalizeCampaignContentSlot } from "@runcheetah/shared";

import { trpc } from "@/lib/trpc";
import { formatShortDate } from "@/lib/date-format";

import type { CampaignReviewPlannedPost } from "./types";

export function useAdminCampaignReviewData({
  brandId,
  campaignId,
}: {
  brandId?: string;
  campaignId?: string;
}) {
  const brandQuery = trpc.admin.brands.get.useQuery({ id: brandId ?? "" }, { enabled: !!brandId });
  const campaignQuery = trpc.admin.campaigns.get.useQuery(
    { brandId: brandId ?? "", campaignId: campaignId ?? "" },
    { enabled: !!brandId && !!campaignId },
  );
  const tasksQuery = trpc.admin.brands.socialPostGenerationTasks.useQuery(
    { brandId: brandId ?? "", campaignId: campaignId ?? "" },
    {
      enabled: !!brandId && !!campaignId,
      refetchInterval: (query) => {
        const tasks = query.state.data;
        return tasks?.some((task) => task.status === "queued" || task.status === "processing")
          ? 2000
          : false;
      },
    },
  );

  const brand = brandQuery.data;
  const campaign = campaignQuery.data;
  const tasks = useMemo(() => tasksQuery.data ?? [], [tasksQuery.data]);

  const campaignBrief = useMemo(
    () =>
      normalizeCampaignBrief({
        briefJson: campaign && "brief" in campaign ? campaign.brief : undefined,
        goals: campaign && "goals" in campaign ? campaign.goals : undefined,
        audience: campaign && "audience" in campaign ? campaign.audience : undefined,
      }),
    [campaign],
  );

  const attemptCountByScheduledPostId = useMemo(() => {
    const counts = new Map<string, number>();

    for (const task of tasks) {
      if (!task.scheduledPostId) {
        continue;
      }

      counts.set(task.scheduledPostId, (counts.get(task.scheduledPostId) ?? 0) + 1);
    }

    return counts;
  }, [tasks]);

  const plannedPosts = useMemo<CampaignReviewPlannedPost[]>(
    () =>
      (campaign?.scheduledPosts ?? []).map((post) => ({
        ...post,
        slot: normalizeCampaignContentSlot({
          slotJson: "slot" in post ? post.slot : undefined,
        }),
        output:
          "output" in post && post.output
            ? (post.output as CampaignReviewPlannedPost["output"])
            : { latestAttempt: null, latestCompleted: null, latestCopy: null },
        attemptCount: attemptCountByScheduledPostId.get(post.id) ?? 0,
      })),
    [attemptCountByScheduledPostId, campaign?.scheduledPosts],
  );

  const scheduledPostLabels = useMemo(
    () =>
      new Map(
        plannedPosts.map((post) => [
          post.id,
          `${formatShortDate(post.scheduledFor)} - ${post.slot.concept}`,
        ]),
      ),
    [plannedPosts],
  );

  return {
    brandQuery,
    campaignQuery,
    tasksQuery,
    brand,
    campaign,
    tasks,
    campaignBrief,
    plannedPosts,
    scheduledPostLabels,
  };
}
